// This module is a directory, not a single file, because each sibling encodes
// a repo rule Luxon itself does not have an opinion on (ADR-096 Rule 12 bars a
// thin wrapper around library surface, so each file here is justified against
// that rule rather than assumed): the single `DEFAULT_TIME_ZONE` constant and
// the never-throwing resolution of untrusted input and canonical zone identity
// live here; the frozen DST-edge policy lives in `wall-time.ts`; member-local day windows in
// `member-local-day.ts`; and "when does this wall clock next happen" in
// `next-occurrence.ts`. None of the four is a Luxon re-spelling — each fixes a
// place where Luxon's own default answer disagrees with what this repo needs.

import { z } from 'zod';

/**
 * The single repo-wide default IANA zone (ADR-095 Rule 4). ADR-095 names
 * `America/New_York` as the one documented default (a deliberate guess over an
 * honest null, since scheduled delivery needs an instant for every member).
 * Exactly one default literal may exist; feature code MUST NOT introduce a
 * second, and it stays a code constant until the fallback metric justifies
 * promoting it.
 */
export const DEFAULT_TIME_ZONE = 'America/New_York';

/**
 * Whether `value` is an IANA zone the repo supports: a region-qualified name
 * (contains `/`) or `UTC`. Region-less abbreviations (`GMT`, `EST`) and
 * malformed names (`Mars/Olympus`) are rejected. This is the single predicate
 * the reminder contract validates against, so a value it accepts is safe both
 * to persist as `schedule.timezone` and to hand to Luxon zone math.
 */
export function isSupportedIanaTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return value.includes('/') || value === 'UTC';
  } catch {
    return false;
  }
}

/**
 * The single shared timezone resolver (ADR-095 Rule 3). Maps a member- or
 * device-reported zone value to a concrete, repo-supported IANA zone, never
 * throwing and never falling back to a process or database zone. A nullish,
 * blank, abbreviated, or malformed value resolves to {@link DEFAULT_TIME_ZONE}
 * rather than flowing through — so the result is always schema-valid and safe
 * for zone math. Feature code MUST route zone resolution through this instead
 * of inlining a `?? 'UTC'` / `|| undefined` fallback.
 */
export function resolveTimeZone(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_TIME_ZONE;
  const trimmed = value.trim();
  return isSupportedIanaTimezone(trimmed) ? trimmed : DEFAULT_TIME_ZONE;
}

/** Zone identity, including IANA aliases; equal offsets alone do not make zones equal. */
export function areTimeZonesEquivalent(left: string, right: string): boolean {
  if (!isSupportedIanaTimezone(left) || !isSupportedIanaTimezone(right)) return false;
  return (
    new Intl.DateTimeFormat('en-US', { timeZone: left }).resolvedOptions().timeZone ===
    new Intl.DateTimeFormat('en-US', { timeZone: right }).resolvedOptions().timeZone
  );
}

/**
 * The single shared IANA timezone schema (ADR-095 Rule 1). Validates any
 * persisted or transported zone value; offsets (`-05:00`, `+0530`) and
 * abbreviations (`EST`, `GMT`) are rejected by {@link isSupportedIanaTimezone}.
 * Re-exported from `reminders/schema.ts` so existing reminder consumers keep
 * compiling — no second timezone schema may be declared in any workspace.
 */
export const ianaTimezoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isSupportedIanaTimezone, 'timezone must be a supported IANA timezone');

export { instantForWallTimeInZone, type WallTime } from './wall-time';
export {
  memberLocalDayWindow,
  memberLocalDayWindowFor,
  memberLocalDayIso,
  type LocalDate,
  type MemberLocalDayWindow,
} from './member-local-day';
export { ZONE_DRIFT_FIXTURES, type ZoneDriftFixture } from './zone-drift.fixtures';
export { nextOccurrence, type TimeOfDay } from './next-occurrence';
